import { Form, Formik, FormikErrors, FormikProps } from "formik";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import datepicker from "@df/js/components/datepicker";
import dropdown from "@df/js/components/dropdown";
import { mapsAutocomplete } from "@df/js/components/maps-autocomplete";
import tooltip from "@df/js/components/tooltip";
import { operations } from "@types/Field";
import { api, utils } from "../../helpers";
import {
  writeSharedToUrl,
  readSharedFromUrl,
  mergeWithDefaults,
} from "../../helpers/forms/share";
import FormFieldWithContext from "./FormFieldWithContext";
import { useForm } from "./hooks/useForm";
import { useResult as useResultHook } from "./hooks/useResult";
import Cta from "@components/Cta/Cta.tsx";
import useDevice from "../../helpers/useDevice.ts";
import { __ } from "@wordpress/i18n";

interface ToolFormWithContextProps {
  listeners?: React.ComponentType[];
  beforeSubmit?: React.ComponentType[];
  extraField?: React.ComponentType<{ field: any }>[];
}

type FormValues = Record<string, any>;

interface ToolField {
  name?: string;
  type?: string;
  defaultValue?: unknown;
  usesAutocomplete?: boolean;
  condition?: { field: string };
  tooltip?: unknown;
  half?: boolean;
  full?: boolean;
}

interface ToolApiConfig {
  method?: string;
  url?: string;
  headers?: Record<string, string>;
  processType?: string;
  operationType?: string;
}

interface ToolData {
  fields?: ToolField[];
  api?: ToolApiConfig;
  validation?: any;
}

interface ResultDataState {
  userDriven?: boolean;
  response: any;
  loading: boolean;
  statusCode: number | null;
  justResult?: boolean;
}

declare global {
  interface Window {
    setFormikFieldValue?: (
      setFieldValue: FormikProps<FormValues>["setFieldValue"],
    ) => void;
    globalSetFieldValue?: FormikProps<FormValues>["setFieldValue"];
    globalSetTouched?: FormikProps<FormValues>["setTouched"];
    globalSetFieldValueSetValues?: FormikProps<FormValues>["setValues"];
    formikValues?: FormValues;
  }
}

const TOOL_BLOCK_SELECTOR = '[data-block="tool-block"]';
const SHARE_PARAM_NAME = "data";
const TOOL_FORM_VALUES_COOKIE = "toolFormValues";
const FIELD_SCROLL_OFFSET = 100;

const getToolBlockElement = (): HTMLElement | null =>
  document.querySelector(TOOL_BLOCK_SELECTOR);

const safeInitUi = (fields?: ToolField[]) => {
  try {
    tooltip.closeAll();
    tooltip.init();
    dropdown.init();
    datepicker.init();
  } catch {
    // ignore
  }

  const hasMapsAutocompleteField = fields?.some((field) => field.usesAutocomplete);
  if (!hasMapsAutocompleteField) return;

  try {
    mapsAutocomplete.init();
  } catch {
    // ignore
  }
};

const getSharedPayloadFromUrl = () => {
  const payloadFromQuery = readSharedFromUrl({
    paramName: SHARE_PARAM_NAME,
    target: "query",
  });

  const payloadFromHash = readSharedFromUrl({
    paramName: SHARE_PARAM_NAME,
    target: "hash",
  });

  return payloadFromQuery || payloadFromHash;
};

const removeLegacyResultParamFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("result");
    window.history.replaceState({}, "", url.toString());
  } catch {
    // ignore
  }
};

const hasShareSimulationEnabled = (): boolean => {
  const rootElement = getToolBlockElement();
  if (!rootElement?.dataset?.attributes) return false;

  try {
    const parsedAttributes = JSON.parse(rootElement.dataset.attributes);
    return parsedAttributes.enableShareSimulation === true;
  } catch {
    return false;
  }
};

const normalizeSubmittedValues = (values: FormValues): FormValues =>
  Object.fromEntries(
    Object.entries(values).filter(([_, value]) => value !== ""),
  );

const buildSafeInitialValues = (
  fields: ToolField[] = [],
  data: ToolData,
): FormValues => {
  const initialValues = utils.generateInitialValues(fields) || {};

  if (data.api?.processType) {
    initialValues.processType = data.api.processType;
  }

  if (data.api?.operationType) {
    initialValues.operationType = data.api.operationType;
  }

  fields.forEach((field) => {
    if (!field.name || initialValues[field.name] !== undefined) return;

    switch (field.type) {
      case "number":
        initialValues[field.name] = field.defaultValue ?? 0;
        break;
      case "select":
      case "radio":
        initialValues[field.name] = field.defaultValue ?? "";
        break;
      case "checkbox":
        initialValues[field.name] = field.defaultValue ?? false;
        break;
      default:
        initialValues[field.name] = field.defaultValue ?? "";
        break;
    }
  });

  return initialValues;
};

const getFirstErrorFieldName = (
  errors: FormikErrors<FormValues>,
): string | null => {
  const keys = Object.keys(errors);
  return keys.length > 0 ? keys[0] : null;
};

const scrollToFieldByName = (fieldName: string) => {
  const field = document.querySelector(`[name="${fieldName}"]`);
  if (!field) return;

  const pos =
    field.getBoundingClientRect().top +
    window.pageYOffset -
    FIELD_SCROLL_OFFSET;

  window.scrollTo({ top: pos, behavior: "smooth" });
};

interface FormContentProps extends FormikProps<FormValues> {
  data: ToolData;
  listeners: React.ComponentType[];
  beforeSubmit?: React.ComponentType[];
  extraField?: React.ComponentType<{ field: any }>[];
  hasSubmitted: boolean;
  submittedValues: FormValues | null;
  loading: boolean;
  updateResultData: (next: Partial<ResultDataState>) => void;
  markNextSubmitAsUserDriven: () => void;
}

const FormContent: React.FC<FormContentProps> = ({
                                                   data,
                                                   listeners,
                                                   extraField,
                                                   beforeSubmit,
                                                   hasSubmitted,
                                                   submittedValues,
                                                   loading,
                                                   updateResultData,
                                                   markNextSubmitAsUserDriven,
                                                   errors,
                                                   touched,
                                                   values,
                                                   setValues,
                                                   setFieldValue,
                                                   setTouched,
                                                   validateForm,
                                                   submitForm,
                                                 }) => {
  useEffect(() => {
    window.globalSetFieldValue = setFieldValue;

    return () => {
      delete window.globalSetFieldValue;
    };
  }, [setFieldValue]);

  useEffect(() => {
    window.globalSetTouched = setTouched;

    return () => {
      delete window.globalSetTouched;
    };
  }, [setTouched]);

  useEffect(() => {
    window.globalSetFieldValueSetValues = setValues;
    window.formikValues = values;

    return () => {
      delete window.globalSetFieldValueSetValues;
      delete window.formikValues;
    };
  }, [setValues, values]);

  useEffect(() => {
    safeInitUi(data.fields);
  }, [data.fields]);

  useEffect(() => {
    const isConditionMet = (cond: any): boolean => {
      const { field: fieldName, value, operator = "===" } = cond;
      return operations[operator as keyof typeof operations]?.(values[fieldName], value) ?? false;
    };

    const hasVisibleConditionalField = data.fields?.some((field: any) => {
      if (field.condition) {
        const conds = Array.isArray(field.condition) ? field.condition : [field.condition];
        return conds.some(isConditionMet);
      }
      if (field.conditionsAnd) {
        return field.conditionsAnd.every(isConditionMet);
      }
      return false;
    });

    if (!hasVisibleConditionalField) return;

    const timeoutId = window.setTimeout(() => {
      try {
        tooltip.closeAll();
      } catch {
        // ignore
      }
      try {
        tooltip.init();
      } catch {
        // ignore
      }
      try {
        dropdown.init();
      } catch {
        // ignore
      }
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [data.fields, values]);

  const isSameValues = useMemo(() => {
    if (!submittedValues) return false;
    return JSON.stringify(values) === JSON.stringify(submittedValues);
  }, [values, submittedValues]);

  const isSubmitDisabled = loading || (hasSubmitted && isSameValues);

  const buttonClasses = useMemo(() => {
    const buttonBaseClasses = "btn w-full transition-opacity duration-300";
    const buttonEnabledClasses = "btn--blue-100";
    const buttonDisabledClasses =
      "btn--blue-10 text-blue-30 cursor-not-allowed opacity-60";

    return `${buttonBaseClasses} ${
      isSubmitDisabled ? buttonDisabledClasses : buttonEnabledClasses
    }`;
  }, [isSubmitDisabled]);

  const handleButtonClick = useCallback(async () => {
    if (isSubmitDisabled) return;

    updateResultData({
      response: null,
      statusCode: null,
    });

    const validationErrors = await validateForm();

    if (Object.keys(validationErrors).length > 0) {
      const touchedFields = Object.keys(validationErrors).reduce<
        Record<string, boolean>
      >((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});

      await setTouched(touchedFields, true);

      const firstErrorName = getFirstErrorFieldName(validationErrors);
      if (firstErrorName) {
        scrollToFieldByName(firstErrorName);
      }

      return;
    }

    markNextSubmitAsUserDriven();
    await submitForm();
  }, [
    isSubmitDisabled,
    updateResultData,
    validateForm,
    setTouched,
    submitForm,
    markNextSubmitAsUserDriven,
  ]);

  return (
    <Form className="grid grid-cols-2 gap-8">
      {listeners.map((ListenerComponent, index) => (
        <ListenerComponent key={index} />
      ))}

      {data.fields?.map((field: any, index: number) => (
        <React.Fragment key={field.name || index}>
          <FormFieldWithContext
            errors={errors}
            touched={touched}
            field={field}
            values={values}
            index={index}
            hasSubmitted={hasSubmitted}
          />
          {extraField?.map((ExtraField, idx) => (
            <ExtraField key={`${field.name || index}-${idx}`} field={field} />
          ))}
        </React.Fragment>
      ))}

      <div className="tool-form__form__field__submit sticky bottom-0 col-span-2">
        {beforeSubmit?.map((BeforeSubmit, index) => (
          <BeforeSubmit key={index} />
        ))}

        <button
          disabled={isSubmitDisabled}
          type="button"
          onClick={handleButtonClick}
          className={buttonClasses}
        >
          {__("Simular", "df-simulators")}
        </button>
      </div>
    </Form>
  );
};

const ToolFormWithContext: React.FC<ToolFormWithContextProps> = ({
                                                                   listeners = [],
                                                                   beforeSubmit = [],
                                                                   extraField = [],
                                                                 }) => {
  const [defaultValues, setDefaultValues] = useState<FormValues | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedValues, setSubmittedValues] = useState<FormValues | null>(null);

  const isSubmittingRef = useRef(false);
  const autofillProcessedRef = useRef(false);
  const hasUserSubmittedRef = useRef(false);

  const { device } = useDevice();
  const { formData, updateFormData } = useForm();
  const { resultData, updateResultData } = useResultHook();

  const data: ToolData = formData.data || {};
  const loading = resultData.loading;

  const formIdentity = useMemo(
    () =>
      JSON.stringify({
        fieldNames: (data.fields || []).map((field) => field.name || ""),
        processType: data.api?.processType || "",
        operationType: data.api?.operationType || "",
        url: data.api?.url || "",
      }),
    [data.fields, data.api?.processType, data.api?.operationType, data.api?.url],
  );

  const safeInitialValues = useMemo(() => {
    if (!data.fields?.length) return null;
    return buildSafeInitialValues(data.fields, data);
  }, [data]);

  const markNextSubmitAsUserDriven = useCallback(() => {
    hasUserSubmittedRef.current = true;
  }, []);

  const updateDropdownCalendarValues = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<{ name: string; value: unknown }>;
    const { name, value } = customEvent.detail || {};

    if (!name || !window.globalSetFieldValue) return;
    window.globalSetFieldValue(name, value);
  }, []);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;

      // Captured before the ref gets reset below, so it survives for the
      // updateResultData calls that happen after the API call resolves.
      const isUserDriven = hasUserSubmittedRef.current;

      try {
        setHasSubmitted(true);
        setSubmittedValues(values);

        if (hasShareSimulationEnabled()) {
          writeSharedToUrl(values, {
            paramName: SHARE_PARAM_NAME,
            replace: true,
          });
        }

        hasUserSubmittedRef.current = false;

        updateFormData({
          submitted: normalizeSubmittedValues(values),
        });

        updateResultData({
          response: null,
          statusCode: null,
          loading: true,
          userDriven: isUserDriven,
        });

        const response = await api({
          method: data.api?.method || "POST",
          url: data.api?.url,
          headers: data.api?.headers,
          data: JSON.stringify(values),
        });

        writeSharedToUrl(values, {
          paramName: SHARE_PARAM_NAME,
          replace: true,
          target: "hash",
        });

        writeSharedToUrl(values, {
          paramName: SHARE_PARAM_NAME,
          replace: true,
        });

        removeLegacyResultParamFromUrl();

        updateResultData({
          response,
          loading: false,
          statusCode: 200,
          userDriven: isUserDriven,
        });

        window.dispatchEvent(
          new CustomEvent("simulationLoaded", {
            detail: { response },
          }),
        );
      } catch (error: any) {
        updateResultData({
          response: error?.response ?? null,
          loading: false,
          statusCode: error?.response?.status ?? null,
          userDriven: isUserDriven,
        });
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [data.api, updateFormData, updateResultData],
  );

  useEffect(() => {
    autofillProcessedRef.current = false;
    isSubmittingRef.current = false;
    hasUserSubmittedRef.current = false;
    setDefaultValues(null);
    setHasSubmitted(false);
    setSubmittedValues(null);
  }, [formIdentity]);

  useEffect(() => {
    window.setFormikFieldValue = (setFieldValue) => {
      window.globalSetFieldValue = setFieldValue;
    };

    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get(SHARE_PARAM_NAME);

    if (!dataParam) {
      utils.cookie.delete(TOOL_FORM_VALUES_COOKIE);
    }

    return () => {
      delete window.setFormikFieldValue;
      delete window.globalSetFieldValue;
      delete window.globalSetFieldValueSetValues;
      delete window.formikValues;
    };
  }, []);

  useEffect(() => {
    window.addEventListener(
      "dateChanged",
      updateDropdownCalendarValues as EventListener,
    );
    window.addEventListener(
      "dropboxChanged",
      updateDropdownCalendarValues as EventListener,
    );

    return () => {
      window.removeEventListener(
        "dateChanged",
        updateDropdownCalendarValues as EventListener,
      );
      window.removeEventListener(
        "dropboxChanged",
        updateDropdownCalendarValues as EventListener,
      );
    };
  }, [updateDropdownCalendarValues]);

  useEffect(() => {
    if (!safeInitialValues) return;
    if (autofillProcessedRef.current) return;

    let cancelled = false;

    const initializeForm = async () => {
      try {
        const payload = getSharedPayloadFromUrl();

        if (!payload) {
          if (!cancelled) {
            setDefaultValues(safeInitialValues);
          }
          autofillProcessedRef.current = true;
          return;
        }

        const mergedValues = mergeWithDefaults(safeInitialValues, payload);

        if (cancelled) return;

        setDefaultValues(mergedValues);
        autofillProcessedRef.current = true;

        updateFormData({
          submitted: mergedValues,
        });

        try {
          await data.validation?.validate(mergedValues, { abortEarly: false });

          if (!cancelled) {
            await onSubmit(mergedValues);
          }
        } catch {
          // Invalid shared payload. Keep merged values so the user can fix them.
        }
      } catch (error) {
        console.error("URL autofill error", error);

        if (!cancelled) {
          setDefaultValues(safeInitialValues);
        }

        autofillProcessedRef.current = true;
      }
    };

    void initializeForm();

    return () => {
      cancelled = true;
    };
  }, [safeInitialValues, data.validation, onSubmit, updateFormData]);

  const hasFormData = Boolean(
    defaultValues &&
    data.fields &&
    Array.isArray(data.fields) &&
    data.fields.length > 0 &&
    data.validation &&
    Object.keys(defaultValues).length > 0,
  );

  if (!hasFormData) {
    return (
      <div className="tool-form">
        <div className="flex items-center justify-center p-8">
          <div className="text-gray-500">A preparar formulário...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tool-form">
      <Formik
        enableReinitialize
        validationSchema={data.validation}
        initialValues={defaultValues}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <FormContent
            {...formikProps}
            data={data}
            listeners={listeners}
            extraField={extraField}
            beforeSubmit={beforeSubmit}
            hasSubmitted={hasSubmitted}
            submittedValues={submittedValues}
            loading={loading}
            updateResultData={updateResultData}
            markNextSubmitAsUserDriven={markNextSubmitAsUserDriven}
          />
        )}
      </Formik>

      {!hasSubmitted && device !== "desktop" && (
        <div className="lg:hidden mt-8">
          <Cta />
        </div>
      )}
    </div>
  );
};

export default ToolFormWithContext;
